import {
  Banner,
  Button,
  Checkbox,
  ColorsV2,
  Divider,
  Flex,
  Link,
  ModalAction,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalV2,
  Spacer,
  Text,
  spaces,
} from '@electricjs/arc';
import { Trans, useTranslation } from 'react-i18next';

import { CenteredSpinner, ExternalLink } from '@common';
import safelyOpenExternalURL from 'common/safelyOpenExternalURL';
import { getLocalizedMsaUrl } from '@/helpers/getLocalizedMsaUrl';
import { ROUTES } from '@/constants/routes';
import { NavigationParamKeys } from '@/types/queryParamKeys';

import PlanLicenseSection from './PlanLicenseSection';
import { useAddLicensesModalState } from './useAddLicensesModalState';

type AddLicensesFormProps = {
  onClose: () => void;
  onSuccess?: () => void;
};

const SUPPORT_EMAIL = 'product-support@electric.ai';
const INVOICES_URL = `${ROUTES.SETTINGS_BILLING}?${NavigationParamKeys.BillingTab}=invoices`;

const AddLicensesForm = ({ onClose, onSuccess }: AddLicensesFormProps) => {
  const { t } = useTranslation('people');

  const handlePayInvoices = () => {
    safelyOpenExternalURL(INVOICES_URL);
  };

  const {
    eligiblePlans,
    isLoading,
    hasPendingSubscriptionChange,
    hasTooManyPastDueInvoices,
    getNewCount,
    setNewCount,
    isValidNewCount,
    msaChecked,
    setMsaChecked,
    totalDelta,
    getChangedPlans,
    isSubmitting,
    isSubmitEnabled,
    submissionError,
    succeededPlanDeltas,
    handleSubmit,
  } = useAddLicensesModalState(onClose, onSuccess);

  const isMultiPlan = eligiblePlans.length > 1;
  const changedPlans = getChangedPlans();

  const singlePlan =
    !isMultiPlan && eligiblePlans.length === 1 ? eligiblePlans[0] : null;

  const titleKey = singlePlan
    ? 'addLicensesModal.titleSingle'
    : 'addLicensesModal.titleMulti';
  const titleProps = singlePlan
    ? { planName: singlePlan.package.displayName }
    : undefined;

  return (
    <ModalV2 ariaLabelledby="add-licenses-modal-header" hide={onClose} visible>
      <ModalHeader>
        <Flex vertical width="100%" rowGap={spaces.space_xs_rem}>
          <Text id="add-licenses-modal-header" variant="heading-2">
            {t(titleKey, titleProps)}
          </Text>
          {isMultiPlan && <Text>{t('addLicensesModal.subtitleMulti')}</Text>}
        </Flex>
      </ModalHeader>
      <ModalBody overflowY="auto">
        {isLoading ? (
          <Flex height="20rem" justifyContent="center" alignItems="center">
            <CenteredSpinner mt="0" />
          </Flex>
        ) : (
          <>
            {hasPendingSubscriptionChange ? (
              <Banner
                id="add-licenses-pending-subscription-change-banner"
                width="100%"
                intent="warning"
                isFlat
                message={() => (
                  <Text variant="body-2">
                    <Trans
                      i18nKey="addLicensesModal.pendingSubscriptionChangeBanner"
                      ns="people"
                      values={{ supportEmail: SUPPORT_EMAIL }}
                      components={{
                        supportLink: (
                          <Link
                            href={`mailto:${SUPPORT_EMAIL}`}
                            variant="body-2">
                            {SUPPORT_EMAIL}
                          </Link>
                        ),
                      }}
                    />
                  </Text>
                )}
              />
            ) : (
              <>
                {hasTooManyPastDueInvoices && (
                  <>
                    <Banner
                      id="add-licenses-billing-error-banner"
                      width="100%"
                      intent="error"
                      isFlat
                      message={t('addLicensesModal.billingErrorBanner')}
                      cta={
                        <Button
                          id="add-licenses-billing-error-banner-cta"
                          variant="outline"
                          intent="danger"
                          onClick={handlePayInvoices}>
                          {t('addLicensesModal.billingErrorBannerCta')}
                        </Button>
                      }
                    />
                    <Spacer space={spaces.space_m} />
                  </>
                )}

                {submissionError && (
                  <>
                    <Banner
                      id="add-licenses-submission-error-banner"
                      width="100%"
                      intent="error"
                      isFlat
                      message={submissionError}
                    />
                    <Spacer space={spaces.space_m} />
                  </>
                )}

                {eligiblePlans.map((plan, index) => {
                  const newCount = getNewCount(plan.id);
                  const isValid = isValidNewCount(plan.id);
                  const newCountInt = parseInt(newCount, 10);
                  const delta =
                    isValid && !isNaN(newCountInt) ? newCountInt : 0;

                  return (
                    <div key={plan.id}>
                      {index > 0 && (
                        <>
                          <Spacer space={spaces.space_m} />
                          <Divider />
                          <Spacer space={spaces.space_m} />
                        </>
                      )}
                      <PlanLicenseSection
                        plan={plan}
                        newCount={newCount}
                        onCountChange={value => setNewCount(plan.id, value)}
                        isValid={isValid}
                        delta={delta}
                        showHeader
                        succeededDelta={succeededPlanDeltas[plan.id]}
                      />
                    </div>
                  );
                })}

                {isMultiPlan && changedPlans.length > 0 && totalDelta > 0 && (
                  <>
                    <Spacer space={spaces.space_m} />
                    <Flex
                      backgroundColor={ColorsV2.GRAY_LIGHTER}
                      borderRadius="0.8rem"
                      px={spaces.space_m_rem}
                      py={spaces.space_s_rem}>
                      <Text>
                        {t('addLicensesModal.totalSummary', {
                          count: totalDelta,
                          plans: changedPlans.length,
                        })}
                      </Text>
                    </Flex>
                  </>
                )}

                <Spacer space={spaces.space_l} />

                <Flex alignItems="flex-start" columnGap={spaces.space_s_rem}>
                  <Checkbox
                    id="add-licenses-msa-checkbox"
                    checked={msaChecked}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setMsaChecked(e.target.checked)
                    }
                  />
                  <Text variant="legal-bold">
                    {t('addLicensesModal.msaCheckboxPrefix')}{' '}
                    <ExternalLink
                      href={getLocalizedMsaUrl()}
                      showIcon={false}
                      variant="legal-bold">
                      {t('addLicensesModal.msaLink')}
                    </ExternalLink>{' '}
                    {t('addLicensesModal.msaCheckboxSuffix')}
                  </Text>
                </Flex>

                <Spacer space={spaces.space_m} />

                <Text variant="legal" intent="secondary">
                  {t('addLicensesModal.billingCycleNote')}
                </Text>
              </>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <ModalFooterActions>
          <ModalAction
            id="add-licenses-cancel-button"
            variant="text"
            disabled={isSubmitting}
            onClick={onClose}>
            {t('addLicensesModal.cancel')}
          </ModalAction>
          {!hasPendingSubscriptionChange && (
            <ModalAction
              id="add-licenses-submit-button"
              loading={isSubmitting}
              disabled={!isSubmitEnabled}
              onClick={handleSubmit}>
              {t('addLicensesModal.addLicensesButton')}
            </ModalAction>
          )}
        </ModalFooterActions>
      </ModalFooter>
    </ModalV2>
  );
};

export default AddLicensesForm;
